import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { UiLinearLoading } from 'ui-components';

export function PageSuspense({ children }) {
  return <Suspense fallback={<UiLinearLoading />}>{children}</Suspense>;
}

PageSuspense.propTypes = {
  children: PropTypes.node,
};

export const Dashboard = lazy(() => import('../pages/dashboard/Dashboard'));
export const Login = lazy(() => import('../pages/user/login/Login'));
export const Register = lazy(() => import('../pages/user/register/Register'));
export const OnboardingContainer = lazy(() =>
  import('../pages/onboarding/OnboardingContainer')
);
export const ChangeLog = lazy(() => import('../pages/change-log/ChangeLog'));
export const AutoBulletin = lazy(() =>
  import('../pages/bulletin/auto-bulletin/AutoBulletinPage')
);
export const SingleAutoBulletin = lazy(() =>
  import('../pages/bulletin/auto-bulletin/SingleAutoBulletinPage')
);

export const WebBulletin = lazy(() =>
  import('../pages/bulletin/web-bulletin/WebBulletin')
);

export const ChangePassword = lazy(() =>
  import('../pages/user/change-password/ChangePassword')
);
export const ForgotPassword = lazy(() =>
  import('../pages/user/forgot-password/ForgotPassword')
);
export const Search = lazy(() => import('../pages/search/Search'));
export const Resource = lazy(() => import('../pages/resource/Resource'));
export const Content = lazy(() => import('../pages/content/Content'));
export const BulletinSettings = lazy(() =>
  import('../pages/bulletin/settings/BulletinSettings')
);
export const SiteSettings = lazy(() =>
  import('../pages/settings/SiteSettings')
);
export const BulletinList = lazy(() =>
  import('../pages/bulletin/list/BulletinList')
);
export const AutoBulletinContentList = lazy(() =>
  import('../pages/bulletin/list/AutoBulletinContentList')
);
export const BulletinContent = lazy(() =>
  import('../pages/bulletin/content/BulletinContent')
);
export const BulletinArchiveList = lazy(() =>
  import('../pages/bulletin/archive-list/BulletinArchiveList')
);
export const TagList = lazy(() => import('../pages/tag/list/TagList'));
export const Notifications = lazy(() =>
  import('../pages/notifications/Notifications')
);
export const Resources = lazy(() => import('../pages/resources/Resources'));
export const ResourceCategoryAdd = lazy(() =>
  import('../pages/resources/categories/add/ResourceCategoryAdd')
);
export const ResourceCategoryManage = lazy(() =>
  import('../pages/resources/categories/manage/ResourceCategoryManage')
);
export const ResourceVipList = lazy(() =>
  import('../pages/resources/vip/manage/ResourceVipManage')
);
export const ResourceVipAdd = lazy(() =>
  import('../pages/resources/vip/manage/ResourceVipManage')
);
export const ContentProfile = lazy(() =>
  import('../pages/profile/content/ContentProfile')
);
export const ResourceProfile = lazy(() =>
  import('../pages/profile/resource/ResourceProfile')
);
export const StatisticsSubject = lazy(() =>
  import('../pages/statistics/subject/StatisticsSubject')
);
export const Pdf = lazy(() => import('../pages/bulletin/pdf/Pdf'));
export const Kiosk = lazy(() => import('../pages/kiosk/items/KioskItems'));
export const Subject = lazy(() => import('../pages/subject/list/SubjectList'));
export const SubjectAdd = lazy(() =>
  import('../pages/subject/manage/SubjectAdd')
);
export const SubjectEdit = lazy(() =>
  import('../pages/subject/manage/SubjectEdit')
);
export const BrandBuilder = lazy(() =>
  import('../pages/brand-builder/BrandBuilder')
);
export const IndustryBuilder = lazy(() =>
  import('../pages/industry-builder/IndustryBuilder')
);
export const AlarmAdd = lazy(() => import('../pages/alarm/manage/AlarmAdd'));
export const AlarmEdit = lazy(() => import('../pages/alarm/manage/AlarmEdit'));
export const AlarmList = lazy(() => import('../pages/alarm/list/AlarmList'));
export const Support = lazy(() => import('../pages/support/Support'));
export const UserTicketList = lazy(() =>
  import('../pages/support/ticket/user/list/UserTicketList')
);
export const UserTicket = lazy(() =>
  import('../pages/support/ticket/user/show/UserTicket')
);
export const Comparison = lazy(() =>
  import('../pages/comparison/list/ComparisonList')
);
export const ComparisonAdd = lazy(() =>
  import('../pages/comparison/add/ComparisonAdd')
);
export const ComparisonSubjectsAdd = lazy(() =>
  import('../pages/comparison/add/subjects/ComparisonSubjectsAdd')
);
export const ComparisonSourcesAdd = lazy(() =>
  import('../pages/comparison/add/sources/ComparisonSourcesAdd')
);
export const ComparisonViewSubject = lazy(() =>
  import('../pages/comparison/view/subjects/ComparisonViewSubject')
);
export const ComparisonViewSource = lazy(() =>
  import('../pages/comparison/view/sources/ComparisonViewSource')
);
export const ResourceVipAdminPermission = lazy(() =>
  import('../pages/resources/vip/admin/list/ResourceVipAdminPermission')
);
export const UsersList = lazy(() => import('../pages/users/list/UserList'));
export const AddUser = lazy(() => import('../pages/users/add/UserAddPage'));
export const EditUser = lazy(() => import('../pages/users/edit/UserEditPage'));
export const AddCompany = lazy(() =>
  import('../pages/companies/add/CompanyAddPage')
);
export const EditCompany = lazy(() =>
  import('../pages/companies/edit/CompanyEditPage')
);
export const CompanyList = lazy(() =>
  import('../pages/companies/list/CompanyListPage')
);
export const AdminTicketList = lazy(() =>
  import('../pages/support/ticket/admin/ticket/list/AdminTicketList')
);
export const AdminTicket = lazy(() =>
  import('../pages/support/ticket/admin/ticket/show/AdminTicket')
);
export const AdminTicketCategoryList = lazy(() =>
  import('../pages/support/ticket/admin/category/list/AdminTicketCategoryList')
);
export const ResourceBlackListedSet = lazy(() =>
  import('../pages/resources/black-list/manage/ResourceBlacklistManage')
);
export const Story = lazy(() => import('../pages/stories/Stories'));

export const Error404 = lazy(() => import('./Error404'));
export const Error403 = lazy(() => import('./Error403'));

export const ShowTranslations = lazy(() => import('../ShowTranslations'));

export const AutoBulletinAdd = lazy(() =>
  import('../pages/bulletin/manage-auto-bulletin/AutoBulletinAdd')
);
export const AutoBulletinEdit = lazy(() =>
  import('../pages/bulletin/manage-auto-bulletin/AutoBulletinEdit')
);
export const TinyUrl = lazy(() => import('../pages/tiny-url/TinyUrlPage'));
export const Academy = lazy(() => import('../pages/academy/list/AcademyPage'));
export const AcademyCourse = lazy(() =>
  import('../pages/academy/item/CourseDetailsPage')
);
export const BrandBaan = lazy(() => import('../pages/brandbaan/BrandBaan'));

export const BrandbaanAllStories = lazy(() =>
  import('../pages/brandbaan/BrandBaanAllStories')
);

export const Ecommerce = lazy(() => import('../pages/ecommerce/Ecommerce'));
export const EcommerceReviewsTable = lazy(() =>
  import('../pages/ecommerce/EcommerceReviewsTable')
);
